import React, { useEffect, useState } from 'react';
import SearchPageSidebarSection from './SearchPageSidebarSection';
import AiOutlineMinus from '../icons/AiOutlineMinus';
import AiOutlinePlus from '../icons/AiOutlinePlus';
import SearchPageSidebarTagGroup from './SearchPageSidebarTagGroup';
import { Col } from 'react-bootstrap';
import { array, bool, func, number, object, oneOfType, string } from 'prop-types';
import { useMemo } from 'react';
import PriceRange from './PriceRange';

/**
 * CONFIGURATION
 */
const SHOW_ALL_LIMIT = 8;

/* Sort types */
const sortMap = {
    SORT_TYPE_DEFAULT: {
        param: '',
        text: 'Default',
    },
    SORT_TYPE_ID_ASC: {
        param: 'id_asc',
        text: 'Older',
    },
    SORT_TYPE_ID_DESC: {
        param: 'id_desc',
        text: 'Newer',
    },
    SORT_TYPE_AZ_ASC: {
        param: 'title_asc',
        text: 'Title: A-Z, ascending',
    },
    SORT_TYPE_AZ_DESC: {
        param: 'title_desc',
        text: 'Title: A-Z, descending',
    },
    SORT_TYPE_PRICE_ASC: {
        param: 'price_asc',
        text: 'Price, lowest to highest',
    },
    SORT_TYPE_PRICE_DESC: {
        param: 'price_desc',
        text: 'Price: highest to lowest',
    },
    SORT_TYPE_TOP_RATED: {
        param: 'top_rated',
        text: 'Product: Top Rated',
    },
    SORT_TYPE_BEST_SELLING: {
        param: 'best_selling',
        text: 'Product: Best Selling',
    },
};

/* Limit Values */
const median = (arr = []) => {
    const sortedArr = [...arr]
        .filter((n) => !isNaN(parseInt(n)))
        .map((n) => parseInt(n));
    const half = Math.floor(sortedArr.length / 2);
    return sortedArr.length % 2 === 0 ?
        sortedArr[half] : (sortedArr[half - 1] + sortedArr[half]) / 2;
};
const limitSet = [8, 16, 24, 48, 64, 76];

/**
 * @function isValidPrice
 * @param {*} price 
 * @param {*} comparePrice 
 * @param {*} allowZero 
 * @returns 
 */
const isValidPrice = (price, comparePrice = null, allowZero = false) =>
    (price === ''
        || price === undefined
        || price === null)
        ? null
        : (!isNaN(parseFloat(price))
            && parseFloat(price) > (isValidPrice(comparePrice)
                ? parseFloat(comparePrice)
                : (allowZero ? -1 : 0)));

export default function SearchPageSidebar({ isMobile, searchQuery, setSearchQuery, vendorList, tagPrefixes, tagList, searchLoading, productCount, productMaxPrice }) {
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [isPriceInvalid, setIsPriceInvalid] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState('');
    const [showAllVendors, setShowAllVendors] = useState(false);
    const [sortType, setSortType] = useState('');
    const [limit, setLimit] = useState('');
    const handlePriceChange = (minPrice, maxPrice) => {
        const minValid = isValidPrice(minPrice, null, true);
        const maxValid = isValidPrice(maxPrice, minPrice, false);
        if (minValid === false
            && maxValid === false) {
            setIsPriceInvalid(true);
            return;
        }
        setSearchQuery({
            ...searchQuery,
            minPrice: minValid ? parseFloat(minPrice).toFixed(2) : '',
            maxPrice: maxValid ? parseFloat(maxPrice).toFixed(2) : '',
        });
    };
    useEffect(() => {
        setMinPrice(searchQuery.minPrice ?? '');
        setMaxPrice(searchQuery.maxPrice ?? '');
    }, [searchQuery.minPrice, searchQuery.maxPrice]);
    const searchVendor = useMemo(() => searchQuery?.v ?? '', [searchQuery]);
    useEffect(() => {
        setSelectedVendor(searchVendor);
    }, [searchVendor]);
    useEffect(() => {
        setSortType(searchQuery.sortBy);
    }, [searchQuery.sortBy]);
    useEffect(() => {
        setLimit(limitSet.includes(parseInt(searchQuery.limit)) ? searchQuery.limit : '');
    }, [searchQuery.limit]);
    const handleVendorChange = (newVendorValue) => {
        setSelectedVendor(newVendorValue);
        setSearchQuery({
            ...searchQuery,
            v: newVendorValue ?? '',
        });
    };
    /* Custom sort */
    const handleSetSortType = (e) => {
        e.preventDefault();
        let { value } = e.target;
        const findValueParamInSortMap = Object.values(sortMap)
            .filter((v) => v.param === value);
        if (!findValueParamInSortMap.length) {
            value = sortMap[Object.keys(sortMap)[0]].param;
            return;
        }
        setSortType(value);
        setSearchQuery({
            ...searchQuery,
            sortBy: value,
        });
    };
    /* End custom sort */
    /* Custom limit */
    const handleSetLimit = (e) => {
        e.preventDefault();
        let { value } = e.target;
        value = parseInt(value);
        if (isNaN(value) || value < Math.min(limitSet) || value > Math.max(limitSet)) {
            value = median(limitSet);
        }
        setLimit(value);
        setSearchQuery({
            ...searchQuery,
            limit: parseInt(value),
        });
    };
    return (<div
        className={'search-page--sidebar'}>
        <SearchPageSidebarSection
            isMobile={isMobile}
            headingText={'Price'}>
            <PriceRange
                minPrice={0}
                maxPrice={productMaxPrice}
                queryMinPrice={minPrice}
                queryMaxPrice={maxPrice}
                handlePriceChange={handlePriceChange}
            />
        </SearchPageSidebarSection>
        <SearchPageSidebarSection
            isMobile={isMobile}
            headingText={'Vendor'}>
            <div
                className={'search-sidebar--input-group'}>
                <input
                    id={'vendor-none'}
                    className={'search-sidebar--radio'}
                    name={'vendor'}
                    type={'radio'}
                    checked={selectedVendor === ''}
                    value={''}
                    onChange={() => handleVendorChange('')} />
                <label
                    htmlFor={'vendor-none'}>
                    <span
                        className={'input-value'}>None</span>
                </label>
            </div>
            {(vendorList.length > 0 && (searchLoading === true || productCount > 0))
                && vendorList
                    .filter((vendor) => {
                        if (productCount > 0) 
                            return true;
                        return selectedVendor !== '' && vendor.vendor === selectedVendor;
                    })
                    .slice(0, showAllVendors === true ? undefined : SHOW_ALL_LIMIT)
                    .map((vendor, idx) => (
                        <div
                            key={idx}
                            className={'search-sidebar--input-group'}>
                            <input
                                id={'vendor-' + idx}
                                className={'search-sidebar--radio'}
                                name={'vendor'}
                                type={'radio'}
                                checked={selectedVendor === vendor.vendor}
                                value={vendor.vendor}
                                onChange={(e) => handleVendorChange(e.target.value)} />
                            <label
                                htmlFor={'vendor-' + idx}>
                                <span
                                    className={'input-value'}>{vendor.vendor}</span>
                                <span
                                    className={'input-count'}>({productCount > 0 ? vendor.vendorCount : 0})</span>
                            </label>
                        </div>
                    ))}
            {(vendorList.length > SHOW_ALL_LIMIT && productCount > 0) && (<span
                className={'show-all-btn'}
                onClick={() => setShowAllVendors(!showAllVendors)}>
                {showAllVendors === false && (<AiOutlinePlus
                    size={'12px'}
                    className={'me-1'} />)}
                {showAllVendors === true && (<AiOutlineMinus
                    size={'12px'}
                    className={'me-1'} />)}
                Show {showAllVendors ? 'less' : 'more'}
            </span>)}
        </SearchPageSidebarSection>
        {tagPrefixes.length > 0 && tagPrefixes.map((tagPrefix, idx) => (<SearchPageSidebarSection
            isMobile={isMobile}
            headingText={tagPrefix.tagPrefixValue}
            key={idx}
            itemCount={tagList.filter((tag) => tag.tagPrefix === tagPrefix.tagPrefixValue).length}>
            <SearchPageSidebarTagGroup
                isMobile={isMobile}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                tagPrefix={tagPrefix.tagPrefixValue}
                tagList={tagList.filter((tag) => tag.tagPrefix === tagPrefix.tagPrefixValue)}
                productCount={productCount} />
        </SearchPageSidebarSection>))}
        <SearchPageSidebarSection
            isMobile={isMobile}
            headingText={'Sorting options'}>
            <Col>
                <label
                    style={{ marginBottom: '10px' }}>
                    <span>Limit:</span>
                    <br />
                    <select
                        className={'search-sidebar--input'}
                        name={'Limit'}
                        value={limit}
                        onChange={handleSetLimit}>
                        <option
                            value={''}>Default</option>
                        {limitSet.map((l) => (
                            <option
                                key={l}
                                value={l}>{l} per page</option>
                        ))}
                    </select>
                </label>
            </Col>
            <Col>
                <label
                    style={{ marginBottom: '10px' }}>
                    <span>Sort by:</span>
                    <br />
                    <select
                        className={'search-sidebar--input'}
                        name={'Sort by'}
                        value={sortType}
                        onChange={handleSetSortType}>
                        {Object.entries(sortMap).map(([k, v]) => (
                            <option
                                key={k}
                                value={v.param}>{v.text}</option>
                        ))}
                    </select>
                </label>
            </Col>
        </SearchPageSidebarSection>
    </div >);
}

SearchPageSidebar.propTypes = { 
    isMobile: bool,
    searchQuery: object,
    setSearchQuery: func,
    vendorList: array,
    tagPrefixes: array,
    tagList: array,
    searchLoading: bool,
    productCount: oneOfType([number, string]),
    productMaxPrice: number,
};